package com.swetdot.swetgram.ui.chat

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.fadeIn
import androidx.compose.animation.fadeOut
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import com.swetdot.swetgram.domain.SquadStreakDomain
import com.swetdot.swetgram.ui.design.Radius
import com.swetdot.swetgram.ui.design.Sizes
import com.swetdot.swetgram.ui.design.Space
import com.swetdot.swetgram.ui.design.SwetIcon
import com.swetdot.swetgram.ui.design.SwetIcons
import com.swetdot.swetgram.ui.theme.SwetgramThemeTokens
import org.jetbrains.compose.resources.stringResource
import swetgram.shared.generated.resources.Res
import swetgram.shared.generated.resources.streak_squad_day_one
import swetgram.shared.generated.resources.streak_squad_days
import swetgram.shared.generated.resources.streak_squad_maintained_today
import swetgram.shared.generated.resources.streak_squad_nudge_btn
import swetgram.shared.generated.resources.streak_squad_start_action
import swetgram.shared.generated.resources.streak_squad_start_prompt
import swetgram.shared.generated.resources.streak_squad_waiting_peer
import swetgram.shared.generated.resources.streak_squad_waiting_you

@Composable
fun SquadStreakBanner(
    streak: SquadStreakDomain?,
    myUserId: String?,
    peerName: String?,
    nudgeInProgress: Boolean,
    startInProgress: Boolean = false,
    onStartStreak: () -> Unit,
    onNudge: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val colors = SwetgramThemeTokens.colors

    Surface(
        color = colors.surface,
        shape = RoundedCornerShape(Radius.sm),
        border = BorderStroke(Sizes.borderHairline, colors.border),
        modifier = modifier
            .fillMaxWidth()
            .padding(horizontal = Space.screenMargin, vertical = Space.half),
    ) {
        if (streak == null) {
            // No active streak yet: discreet start invite
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = Space.two, vertical = Space.one),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween,
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.weight(1f),
                ) {
                    SwetIcon(
                        icon = SwetIcons.Flame,
                        tint = colors.accent,
                        contentDescription = null,
                        size = Sizes.iconSmall,
                    )
                    Spacer(Modifier.width(Space.one))
                    Text(
                        text = stringResource(Res.string.streak_squad_start_prompt),
                        style = MaterialTheme.typography.bodySmall,
                        color = colors.textSecondary,
                    )
                }
                Spacer(Modifier.width(Space.one))
                Surface(
                    onClick = onStartStreak,
                    enabled = !startInProgress,
                    color = colors.graphite,
                    shape = RoundedCornerShape(Radius.full),
                    border = BorderStroke(Sizes.borderHairline, colors.border),
                ) {
                    Row(
                        modifier = Modifier.padding(horizontal = Space.one, vertical = 4.dp),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        if (startInProgress) {
                            CircularProgressIndicator(
                                modifier = Modifier.size(12.dp),
                                strokeWidth = 1.5.dp,
                                color = colors.accent,
                            )
                        } else {
                            Text(
                                text = stringResource(Res.string.streak_squad_start_action),
                                style = MaterialTheme.typography.labelSmall,
                                fontWeight = FontWeight.Bold,
                                color = colors.accent,
                            )
                        }
                    }
                }
            }
        } else {
            // Active squad streak
            val isMyActive = myUserId != null && streak.isParticipantActive(myUserId)
            val allDone = streak.allActiveToday

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = Space.two, vertical = Space.one),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                SwetIcon(
                    icon = SwetIcons.Flame,
                    tint = if (allDone || streak.currentStreak > 0) colors.accent else colors.textSecondary,
                    contentDescription = null,
                    size = Sizes.iconMedium,
                )
                Spacer(Modifier.width(Space.one))

                Column(modifier = Modifier.weight(1f)) {
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        val streakTitle = if (streak.currentStreak == 0) {
                            stringResource(Res.string.streak_squad_day_one)
                        } else {
                            "🔥 " + stringResource(Res.string.streak_squad_days, streak.currentStreak)
                        }
                        Text(
                            text = streakTitle,
                            style = MaterialTheme.typography.labelMedium,
                            fontWeight = FontWeight.Bold,
                            color = colors.textPrimary,
                        )
                    }
                    Spacer(Modifier.height(2.dp))
                    val statusText = when {
                        allDone -> stringResource(Res.string.streak_squad_maintained_today)
                        isMyActive -> stringResource(Res.string.streak_squad_waiting_peer, peerName ?: "")
                        else -> stringResource(Res.string.streak_squad_waiting_you)
                    }
                    Text(
                        text = statusText,
                        style = MaterialTheme.typography.bodySmall,
                        color = if (allDone) colors.accent else colors.textSecondary,
                    )
                }

                if (!allDone) {
                    Spacer(Modifier.width(Space.one))
                    Surface(
                        onClick = onNudge,
                        enabled = !nudgeInProgress,
                        color = colors.graphite,
                        shape = RoundedCornerShape(Radius.full),
                        border = BorderStroke(Sizes.borderHairline, colors.border),
                    ) {
                        Row(
                            modifier = Modifier.padding(horizontal = Space.one, vertical = 4.dp),
                            verticalAlignment = Alignment.CenterVertically,
                        ) {
                            if (nudgeInProgress) {
                                CircularProgressIndicator(
                                    modifier = Modifier.size(12.dp),
                                    strokeWidth = 1.5.dp,
                                    color = colors.accent,
                                )
                            } else {
                                Text(
                                    text = stringResource(Res.string.streak_squad_nudge_btn),
                                    style = MaterialTheme.typography.labelSmall,
                                    fontWeight = FontWeight.SemiBold,
                                    color = colors.textPrimary,
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}
